import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Sparkles, Scissors, Heart, Calendar } from 'lucide-react';
import { siteInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FootprintIcon = () => <span className="text-2xl">🦶</span>;

export const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const icons = {
    Scissors: Scissors,
    Sparkles: Sparkles,
    Footprints: FootprintIcon,
    Heart: Heart,
  };

  useEffect(() => {
    // Charger les témoignages et services depuis l'API
    const fetchData = async () => {
      try {
        const [testimonialsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/services`)
        ]);
        
        // Prendre seulement les 8 meilleurs avis (5 étoiles)
        const topReviews = testimonialsRes.data.testimonials
          .filter(t => t.rating === 5)
          .slice(0, 8);
        setTestimonials(topReviews);
        
        // Filtrer uniquement les services actifs et prendre les 4 premiers
        const activeServices = servicesRes.data.services
          .filter(s => s.isActive)
          .slice(0, 4);
        setServices(activeServices);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1602580404246-225af00637ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwd29tYW4lMjBiZWF1dHl8ZW58MHx8fHwxNzY0NjE1MTk5fDA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-[#C9A96E]/20 backdrop-blur-sm text-[#C9A96E] rounded-full text-sm font-medium mb-6 animate-fade-in">
              Institut de Beauté Afro à Asnières-sur-Seine
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold text-white mb-6 animate-fade-in-up leading-tight">
              Révélez votre
              <span className="block text-[#C9A96E]">Beauté Naturelle</span>
            </h1>
            <p className="text-xl text-[#E8DDD4] mb-10 animate-fade-in-up stagger-2 leading-relaxed">
              Chez {siteInfo.name}, nous célébrons la beauté dans toute sa splendeur. 
              Coiffure afro et européenne, soins du visage, manucure et pédicure par des expertes passionnées depuis plus de 10 ans.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
              <Link
                to="/reservation"
                className="btn-primary px-6 py-3 sm:px-8 sm:py-4 rounded-full text-base sm:text-lg font-semibold inline-flex items-center gap-2"
              >
                <Calendar size={20} />
                Réserver maintenant
              </Link>
              <Link
                to="/services"
                className="btn-secondary px-6 py-3 sm:px-8 sm:py-4 rounded-full text-base sm:text-lg font-semibold inline-flex items-center gap-2 text-white border-white hover:bg-white hover:text-[#2C1810]"
              >
                Nos services
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>

        {/* Floating stats */}
        <div className="hidden lg:flex absolute bottom-10 right-10 gap-6 z-10">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center">
            <p className="text-4xl font-serif font-bold text-[#C9A96E]">500+</p>
            <p className="text-white text-sm">Clientes satisfaites</p>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center">
            <p className="text-4xl font-serif font-bold text-[#C9A96E]">10+</p>
            <p className="text-white text-sm">Années d'expérience</p>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-24 bg-[#FDF8F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Nos Expertises</span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mt-4 mb-6">
              Des Services d'Exception
            </h2>
            <div className="section-divider" />
            <p className="text-[#5D4037] mt-6 max-w-2xl mx-auto text-lg">
              Découvrez notre gamme complète de soins et services dédiés à la beauté afro
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = icons[service.icon] || Sparkles;
              return (
                <Link
                  key={service.id}
                  to="/services"
                  className={`service-card group animate-fade-in-up stagger-${index + 1}`}
                >
                  <div className="img-zoom h-52">
                    <img
                      src={service.image}
                      alt={service.category}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="w-14 h-14 rounded-full bg-[#C9A96E]/10 flex items-center justify-center mb-4 group-hover:bg-[#C9A96E]/20 transition-colors">
                      <IconComponent className="text-[#C9A96E]" size={28} />
                    </div>
                    <h3 className="text-xl font-serif font-semibold text-[#2C1810] mb-2">
                      {service.category}
                    </h3>
                    <p className="text-[#5D4037] text-sm leading-relaxed">
                      {service.description}
                    </p>
                    <div className="mt-4 flex items-center text-[#C9A96E] font-medium text-sm group-hover:gap-3 transition-all">
                      Découvrir <ArrowRight size={16} className="ml-2" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="btn-secondary px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2"
            >
              Voir tous nos services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-[#2C1810] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1688592969417-953dd3c2b9d8')] bg-cover bg-center" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Notre Histoire</span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mt-4 mb-6">
                Faites-vous belle, vous le méritez.
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mb-8" />
              <p className="text-[#D4C4B5] text-lg leading-relaxed mb-6">
                Fondé par Yrène il y a plus de 10 ans, {siteInfo.name} est bien plus qu'un simple institut. 
                C'est un lieu de confiance où chaque cliente est unique et mérite le meilleur.
              </p>
              <p className="text-[#D4C4B5] text-lg leading-relaxed mb-8">
                Notre expertise en coiffure afro et européenne, ainsi que dans les soins des cheveux texturés 
                et des peaux noires, nous permet de vous offrir des prestations parfaitement adaptées à vos besoins.
              </p>
              <Link
                to="/a-propos"
                className="btn-primary px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2"
              >
                En savoir plus
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1688592969417-953dd3c2b9d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwYnJhaWRzfGVufDB8fHx8MTc2NDYxNTIwNXww&ixlib=rb-4.1.0&q=85"
                    alt="Coiffure afro"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1639162906614-0603b0ae95fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxiZWF1dHklMjBzcGElMjB0cmVhdG1lbnR8ZW58MHx8fHwxNzY0NjE1MjExfDA&ixlib=rb-4.1.0&q=85"
                    alt="Soins visage"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1667498235477-0dcfc8593de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxBZnJpY2FuJTIwYnJhaWRzfGVufDB8fHx8MTc2NDYxNTIwNXww&ixlib=rb-4.1.0&q=85"
                    alt="Tresses"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1639531167411-2fbab09f57f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxBZnJpY2FuJTIwd29tYW4lMjBiZWF1dHl8ZW58MHx8fHwxNzY0NjE1MTk5fDA&ixlib=rb-4.1.0&q=85"
                    alt="Beauté"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Témoignages</span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mt-4 mb-6">
              Ce que disent nos clientes
            </h2>
            <div className="section-divider" />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5D4037]">Chargement des avis...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={`testimonial-card card-hover animate-fade-in-up stagger-${index + 1}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#C9A96E] flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#2C1810]">{testimonial.name}</h4>
                      <p className="text-xs text-[#A67B5B]">{testimonial.source || 'Google'}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-[#C9A96E] text-[#C9A96E]" />
                    ))}
                  </div>
                  <p className="text-[#5D4037] text-sm leading-relaxed italic line-clamp-4">
                    "{testimonial.comment}"
                  </p>
                  {testimonial.date && (
                    <p className="text-xs text-[#A67B5B] mt-3">{testimonial.date}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Prête à sublimer votre beauté ?
          </h2>
          <p className="text-white/90 text-xl mb-10">
            Réservez votre rendez-vous en quelques clics et laissez-nous prendre soin de vous
          </p>
          <Link
            to="/reservation"
            className="bg-white text-[#2C1810] px-10 py-5 rounded-full text-lg font-semibold inline-flex items-center gap-3 hover:bg-[#FDF8F3] transition-colors shadow-lg hover:shadow-xl"
          >
            <Calendar size={24} />
            Réserver mon rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
};
